import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MCAPage from './pages/workspace/MCAPage';
import HRPage from './pages/workspace/HRPage';
import ReferralsPage from './pages/workspace/ReferralsPage';
import ComingSoonPage from './pages/workspace/ComingSoonPage';
import DPRPage from './pages/workspace/DPRPage';
import PeoplePage from './pages/workspace/PeoplePage';
import RenewalsPage from './pages/workspace/RenewalsPage';
import SuperAdmin from './pages/SuperAdmin';
import ClientPortalLogin from './pages/client-portal/Login';
import ClientPortalDashboard from './pages/client-portal/Dashboard';
import ClientPortalTasks from './pages/client-portal/Tasks';
import ClientPortalDocuments from './pages/client-portal/Documents';
import ClientPortalLedger from './pages/client-portal/Ledger';
import ClientPortalInvoices from './pages/client-portal/Invoices';
import ClientPortalReferral from './pages/client-portal/Referral';
import { clientPortalFetch } from './pages/client-portal/api';

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/" />;
}

// The dedicated, platform-wide client entry point (e.g. clientlogin.chanakyax.in)
// — not any one firm's domain, so it can't resolve to a firm on its own.
// Visiting its bare root should still show the client-portal shell (which
// then prompts for/expects a slug in the path), never the staff login.
// Blank/unset unless this env var is configured — no effect until then.
const CLIENT_PORTAL_HOST = process.env.REACT_APP_CLIENT_PORTAL_HOST || '';

// "/" has to serve different audiences from one URL: staff opening their
// normal login, a client whose firm registered this exact hostname as their
// portal domain (Settings → Client Portal → Your Own Domain), or the
// platform-wide client-login host above. Nothing is hardcoded per-firm here
// — every hostname is checked against client_portal_settings.custom_domain
// at request time; unmapped hosts fall through to staff Login.
function HomeGate() {
  const [checked, setChecked] = useState(false);
  const [isClientDomain, setIsClientDomain] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;
    if (CLIENT_PORTAL_HOST && host === CLIENT_PORTAL_HOST) {
      setIsClientDomain(true);
      setChecked(true);
      return;
    }
    let cancelled = false;
    clientPortalFetch(`/resolve?host=${encodeURIComponent(host)}`)
      .then(() => { if (!cancelled) setIsClientDomain(true); })
      .catch(() => { /* not a mapped client domain — show staff login */ })
      .finally(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, []);

  if (!checked) return null;
  return isClientDomain ? <ClientPortalLogin /> : <Login />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeGate />} />
        <Route path="/register" element={<Register />} />

        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />

        <Route path="/workspace/mca" element={
          <ProtectedRoute><MCAPage /></ProtectedRoute>
        } />
        <Route path="/workspace/hr" element={
          <ProtectedRoute><HRPage /></ProtectedRoute>
        } />
        <Route path="/workspace/referrals" element={
          <ProtectedRoute><ReferralsPage /></ProtectedRoute>
        } />
        <Route path="/workspace/people" element={
          <ProtectedRoute><PeoplePage /></ProtectedRoute>
        } />
        <Route path="/workspace/renewals" element={
          <ProtectedRoute><RenewalsPage /></ProtectedRoute>
        } />
        <Route path="/workspace/dpr" element={
          <ProtectedRoute><DPRPage /></ProtectedRoute>
        } />
        <Route path="/workspace/income-tax" element={
          <ProtectedRoute><ComingSoonPage moduleKey="income_tax_workspace" moduleName="Income Tax" description="Tax compliance filing, tracking and notice management." /></ProtectedRoute>
        } />
        <Route path="/workspace/gst" element={
          <ProtectedRoute><ComingSoonPage moduleKey="gst_workspace" moduleName="GST" description="GST return filing and compliance." /></ProtectedRoute>
        } />
        <Route path="/mca" element={<Navigate to="/workspace/mca" />} />
        <Route path="/law" element={
          <ProtectedRoute>
            </ProtectedRoute>
        } />

        {/* SuperAdmin panel — completely separate from main app */}
        {/* Protected by secret key + separate JWT — no firm context */}
        <Route path="/superadmin" element={<SuperAdmin />} />
        <Route path="/superadmin/*" element={<SuperAdmin />} />

        {/* Client Portal — completely separate from main app, its own Google */}
        {/* sign-in + client JWT (see clientPortalAuth.js). Multi-tenant: each */}
        {/* firm shares its own /portal/<slug>/login link with clients (set from */}
        {/* Settings → Client Portal) — the slug resolves to a firm server-side, */}
        {/* so two firms' clients are never mixed even if they share a Gmail. */}
        <Route path="/portal/:slug/login" element={<ClientPortalLogin />} />
        {/* Legacy path with no slug — shows a "use your firm's link" message. */}
        <Route path="/client-portal" element={<Navigate to="/client-portal/login" />} />
        <Route path="/client-portal/login" element={<ClientPortalLogin />} />
        <Route path="/client-portal/dashboard" element={<ClientPortalDashboard />} />
        <Route path="/client-portal/tasks" element={<ClientPortalTasks />} />
        <Route path="/client-portal/documents" element={<ClientPortalDocuments />} />
        <Route path="/client-portal/ledger" element={<ClientPortalLedger />} />
        <Route path="/client-portal/invoices" element={<ClientPortalInvoices />} />
        <Route path="/client-portal/referral" element={<ClientPortalReferral />} />

      </Routes>
    </BrowserRouter>
  );
}

export default App;

